/**
 * NH pilot pulse for ITT-972: coverage / proposed / conflicts from existing
 * beerProfileVersions + open/retry signals from the failure list.
 * Does not reuse the Increment 1 pilot scoreboard.
 */

// skipcq: JS-W1028
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { UserContext } from '../context/UserContext';
import { listBeerIngestion } from '../services/beerIngestionService';
import {
  listProfileProcessingFailures,
} from '../services/beerProfileProcessingFailuresService';
import { db } from '../utils/firebase';
import { BEER_PROFILE_VERSIONS_COLLECTION } from '../utils/firestoreCollections';
import {
  buildProfileProcessingPulse,
  countProfileFailureRetryOutcomes,
} from '../utils/brewery/beerProfileProcessingFailures';
import {
  chunkBeerIdsForProfileVersionQuery,
  countBeerProfileShadowSummaries,
  deriveBeerProfileShadowSummary,
  reduceLatestBeerProfileVersionsByBeerId,
} from '../utils/beer/beerProfileComparison';
import { captureProfileProcessingPulseView } from '../utils/beerIngestionAnalytics';

async function fetchBeerIdsForBrewery(breweryId) {
  const beersQuery = query(
    collection(db, 'beer'),
    where('availableBreweryIds', 'array-contains', breweryId),
  );
  const snapshot = await getDocs(beersQuery);
  return snapshot.docs.map((docSnap) => docSnap.id);
}

/**
 * @param {{ enabled?: boolean }} [options]
 */
export default function useNhProfileProcessingPulse(options = {}) {
  const enabled = options.enabled === true;
  const user = useContext(UserContext);
  const isGlobalAdmin = user?.isGlobalAdmin === true;

  const [beerCount, setBeerCount] = useState(0);
  const [trustedBreweryCount, setTrustedBreweryCount] = useState(0);
  const [shadowCounts, setShadowCounts] = useState({
    shadowCount: 0,
    proposedDiffCount: 0,
    conflictCount: 0,
  });
  const [failureSummary, setFailureSummary] = useState({
    open: 0,
    resolved: 0,
    byStage: {},
    reportingAsOf: null,
    reportingStale: false,
  });
  const [openFailureRows, setOpenFailureRows] = useState([]);
  const [resolvedFailureRows, setResolvedFailureRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    if (!enabled || !isGlobalAdmin) {
      setBeerCount(0);
      setTrustedBreweryCount(0);
      setShadowCounts({ shadowCount: 0, proposedDiffCount: 0, conflictCount: 0 });
      setFailureSummary({
        open: 0,
        resolved: 0,
        byStage: {},
        reportingAsOf: null,
        reportingStale: false,
      });
      setOpenFailureRows([]);
      setResolvedFailureRows([]);
      setLoading(false);
      setLoaded(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;

    const loadPulse = async () => {
      setLoading(true);
      setLoaded(false);
      setError(null);
      try {
        const [ingestion, openFailures, resolvedFailures] = await Promise.all([
          listBeerIngestion('NH'),
          listProfileProcessingFailures({ disposition: 'open' }),
          listProfileProcessingFailures({ disposition: 'resolved', limit: 100 }),
        ]);
        if (cancelled || requestId !== requestIdRef.current) {
          return;
        }

        setFailureSummary({
          open: openFailures.summary.open,
          resolved: resolvedFailures.summary.resolved || openFailures.summary.resolved,
          byStage: openFailures.summary.byStage,
          reportingAsOf: openFailures.summary.reportingAsOf,
          reportingStale: openFailures.summary.reportingStale,
        });
        setOpenFailureRows(openFailures.rows);
        setResolvedFailureRows(resolvedFailures.rows);

        const trustedBreweryIds = (Array.isArray(ingestion.rows) ? ingestion.rows : [])
          .filter((row) => row?.derivedStatus === 'trusted' && row?.breweryId)
          .map((row) => row.breweryId);

        setTrustedBreweryCount(trustedBreweryIds.length);

        const beerIdLists = await Promise.all(
          trustedBreweryIds.map((breweryId) => fetchBeerIdsForBrewery(breweryId)),
        );
        if (cancelled || requestId !== requestIdRef.current) {
          return;
        }

        const beerIds = [];
        const seenBeerIds = new Set();
        beerIdLists.forEach((ids) => {
          ids.forEach((beerId) => {
            if (!seenBeerIds.has(beerId)) {
              seenBeerIds.add(beerId);
              beerIds.push(beerId);
            }
          });
        });
        setBeerCount(beerIds.length);

        if (beerIds.length === 0) {
          setShadowCounts({ shadowCount: 0, proposedDiffCount: 0, conflictCount: 0 });
          setLoaded(true);
          return;
        }

        const chunks = chunkBeerIdsForProfileVersionQuery(beerIds);
        const snapshots = await Promise.all(
          chunks.map((chunk) => {
            const versionsQuery = query(
              collection(db, BEER_PROFILE_VERSIONS_COLLECTION),
              where('beerId', 'in', chunk),
            );
            return getDocs(versionsQuery);
          }),
        );
        if (cancelled || requestId !== requestIdRef.current) {
          return;
        }

        const versionDocs = [];
        snapshots.forEach((snapshot) => {
          snapshot.docs.forEach((docSnap) => {
            versionDocs.push({
              id: docSnap.id,
              ...docSnap.data(),
            });
          });
        });

        const latestByBeerId = reduceLatestBeerProfileVersionsByBeerId(versionDocs);
        const summariesByBeerId = new Map();
        latestByBeerId.forEach((version, beerId) => {
          summariesByBeerId.set(beerId, deriveBeerProfileShadowSummary(version));
        });
        setShadowCounts(countBeerProfileShadowSummaries(beerIds, summariesByBeerId));
        setLoaded(true);
      } catch (loadError) {
        if (cancelled || requestId !== requestIdRef.current) {
          return;
        }
        console.error('Failed to load NH profile processing pulse', loadError);
        setError(
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load NH profile processing pulse',
        );
        setBeerCount(0);
        setTrustedBreweryCount(0);
        setShadowCounts({ shadowCount: 0, proposedDiffCount: 0, conflictCount: 0 });
        setFailureSummary({
          open: 0,
          resolved: 0,
          byStage: {},
          reportingAsOf: null,
          reportingStale: false,
        });
        setOpenFailureRows([]);
        setResolvedFailureRows([]);
        setLoaded(false);
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    loadPulse();

    return () => {
      cancelled = true;
    };
  }, [enabled, isGlobalAdmin]);

  const retryOutcomes = useMemo(
    () => countProfileFailureRetryOutcomes([
      ...openFailureRows,
      ...resolvedFailureRows,
    ]),
    [openFailureRows, resolvedFailureRows],
  );

  const pulse = useMemo(
    () => buildProfileProcessingPulse({
      beerCount,
      shadowCount: shadowCounts.shadowCount,
      proposedDiffCount: shadowCounts.proposedDiffCount,
      conflictCount: shadowCounts.conflictCount,
      openFailures: failureSummary.open,
      resolvedFailures: failureSummary.resolved,
      retryOutcomes,
      reportingAsOf: failureSummary.reportingAsOf,
      reportingStale: failureSummary.reportingStale,
      byStage: failureSummary.byStage,
    }),
    [beerCount, shadowCounts, failureSummary, retryOutcomes],
  );

  useEffect(() => {
    if (enabled && loaded && !loading && !error) {
      captureProfileProcessingPulseView(pulse);
    }
  }, [enabled, loaded, loading, error, pulse]);

  return {
    pulse,
    trustedBreweryCount,
    openFailureCount: failureSummary.open,
    loading,
    error,
  };
}
